import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { environment } from '../../../environments/environment';
import {
  APPLICATION_TYPE_OPTIONS,
  DroneFlightEntry,
  FlightPathData,
  PPE_OPTIONS,
  SprayRecord
} from 'src/app/core/models/spray-record.model';
import { FlightMapComponent } from "src/app/shared/components/flight-map/flight-map.component";
import { formatCalendarDate, formatClockTime, toDisplayDate } from 'src/app/shared/utils/date.utils';


interface Field {
  id: string;
  name: string;
  acreage: number;
  crop_type: string;
  latitude: number | null;
  longitude: number | null;
  address?: string;
  customerId?: string;
}

interface Customer {
  id: string;
  name: string;
}

/**
 * Flight Record Detail Component
 * Read-only admin view of a single spray record with all related data.
 * Modeled after the user-side spray-record-details component.
 */
@Component({
  selector: 'app-flight-record-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, FlightMapComponent, PhoneFormatPipe],
  templateUrl: './flight-record-detail.component.html',
  styleUrls: ['./flight-record-detail.component.scss']
})
export class FlightRecordDetailComponent implements OnInit {
  recordId: string | null = null;
  record: SprayRecord | null = null;
  loading = true;
  errorMessage = '';

  field: Field | null = null;
  customer: Customer | null = null;


  // Flight map modal
  showFlightMapModal = false;
  flightMapModalFlight: DroneFlightEntry | null = null;
  showAllFlights = false;

  // Back-to-organization navigation
  fromOrgId: string | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.recordId = this.route.snapshot.paramMap.get('id');
    this.fromOrgId = this.route.snapshot.queryParamMap.get('fromOrg');

    if (this.recordId) {
      this.loadRecord(this.recordId);
    } else {
      this.errorMessage = 'No record ID provided';
      this.loading = false;
    }
  }

  async loadRecord(id: string): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: any }>(
          `${environment.apiUrl}/admin/spray-records/${id}`
        )
      );

      if (response.success && response.data) {
        console.log(response.data)
        this.record = response.data;
        this.field = response.data.field;
        this.customer = response.data.customer;
      } else {
        this.errorMessage = 'Spray record not found';
      }
    } catch (error: any) {
      console.error('Error loading flight record:', error);
      if (error?.status === 404) {
        this.errorMessage = 'Spray record not found';
      } else {
        this.errorMessage = 'Failed to load flight record';
      }
    } finally {
      this.loading = false;
    }
  }

  goBack(): void {
    if (this.fromOrgId) {
      this.router.navigate(['/organizations', this.fromOrgId], {
        queryParams: { tab: 'sprayRecords' }
      });
    } else {
      this.router.navigate(['/flight-records']);
    }
  }

  // Status helpers
  getStatusText(status: string): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  // Chemical source badge
  getSourceBadgeClass(source: string): string {
    switch (source) {
      case 'book': return 'bg-primary-subtle text-primary';
      case 'custom': return 'bg-info-subtle text-info';
      case 'template': return 'bg-success-subtle text-success';
      default: return 'bg-secondary-subtle text-secondary';
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'book': return 'Book';
      case 'custom': return 'Custom';
      case 'template': return 'Template';
      default: return source;
    }
  }

  // Formatting helpers
  /**
   * `application_date` / `certification_expiration` are Postgres `date` columns
   * that the UTC Hub serializes as midnight-UTC. Formatting those through
   * `new Date(...)` rendered the previous day in every US timezone.
   */
  formatDate(date: string): string {
    return formatCalendarDate(
      date,
      { year: 'numeric', month: 'long', day: 'numeric' },
      'N/A'
    );
  }

  /**
   * Flight times arrive as full ISO timestamps ("2026-07-29T10:00:00.000Z"),
   * not bare "HH:MM" strings. Splitting on ':' parsed the *year* as the hour,
   * which is what turned a 10 AM – 2 PM flight into "10:00 PM – 10:00 PM".
   */
  formatTime(time: string): string {
    return formatClockTime(time);
  }

  // Incident helpers
  get hasAnyIncidents(): boolean {
    if (!this.record?.incidentInfo) return false;
    const info = this.record.incidentInfo;
    return info.takeOffOrLandingDamage || info.equipmentMalfunction || info.hasAccident;
  }

  // Totals
  get totalAcreageSprayed(): number {
    if (!this.record?.droneFlights) return 0;
    return this.record.droneFlights.reduce((sum: number, flight: any) => sum + (Number(flight.acreageSprayed) || 0), 0);
  }

  // Application type labels
  //
  // Both label maps are derived from the shared option lists rather than kept
  // by hand. The previous hardcoded maps listed values the spray-record form
  // never writes (aerial/ground/seed-treatment for types, kebab-case slugs for
  // PPE), so every real value fell through and rendered as its raw slug.
  getApplicationTypeLabels(): string[] {
    if (!this.record?.applicationTypeInfo?.applicationTypes) return [];
    const typeLabels = new Map<string, string>(
      APPLICATION_TYPE_OPTIONS.map(o => [o.value as string, o.label as string])
    );
    return this.record.applicationTypeInfo.applicationTypes.map(
      (type: string) => typeLabels.get(type) || type
    );
  }

  // PPE labels
  getPpeLabels(): string[] {
    if (!this.record?.safetyCompliance?.ppeRequired) return [];
    const ppeLabels = new Map<string, string>(
      PPE_OPTIONS.map(o => [o.value as string, o.label as string])
    );
    return this.record.safetyCompliance.ppeRequired.map(
      (ppe: string) => ppeLabels.get(ppe) || ppe
    );
  }

  // Certification helpers
  get certificationName(): string {
    return this.record?.certificationName || 'N/A';
  }

  get certificationNumber(): string {
    return this.record?.certificationNumber || 'N/A';
  }

  get certificationState(): string {
    return this.record?.certificationState || 'N/A';
  }

  get certificationExpiration(): string {
    if (!this.record?.certificationExpiration) return 'N/A';
    return this.formatDate(this.record.certificationExpiration);
  }

  isCertificationExpired(): boolean {
    if (!this.record?.certificationExpiration) return false;
    const expDate = toDisplayDate(this.record.certificationExpiration);
    if (!expDate || isNaN(expDate.getTime())) return false;
    return expDate.getTime() <= Date.now();
  }

  isCertificationExpiringSoon(): boolean {
    if (!this.record?.certificationExpiration) return false;
    const expDate = toDisplayDate(this.record.certificationExpiration);
    if (!expDate || isNaN(expDate.getTime())) return false;
    const now = Date.now();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return expDate.getTime() > now && expDate.getTime() - now <= thirtyDays;
  }

  // Flight map modal
  viewFlightPath(flight: DroneFlightEntry): void {
    this.flightMapModalFlight = flight;
    this.showAllFlights = false;
    this.showFlightMapModal = true;
  }

  viewAllFlightPaths(): void {
    this.flightMapModalFlight = null;
    this.showAllFlights = true;
    this.showFlightMapModal = true;
  }

  closeFlightMapModal(): void {
    this.showFlightMapModal = false;
    this.flightMapModalFlight = null;
    this.showAllFlights = false;
  }

  getAllFlightPaths(): FlightPathData[] {
    if (!this.record) return [];
    return this.record.droneFlights
      .filter(f => f.flightPath !== null)
      .map(f => f.flightPath!);
  }

  getFieldCenter(): { lat: number; lng: number } | null {
    if (this.field?.latitude && this.field?.longitude) {
      return { lat: this.field.latitude, lng: this.field.longitude };
    }
    return null;
  }
}
